// Override der Pricing-Sperre für genau einen Kunden. Schreibt
// overrides[partnerId] im Flag pricing_enforcement des System-Items
// FEATURE_FLAGS, mit saveFlags aus useFeatureFlags, also mit Bedingung auf
// updatedAt wie die Experimente-Seite. Nur für __admin. Auf production
// verlangt das Speichern eine zweite Bestätigung mit dem Namen der Umgebung.
import { useState } from 'react'
import { RadioGroup } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'

import Modal from '../Modal'
import Button from '../Button'
import { Notice } from '../experiments/ExperimentBadges'
import { useNotificationContext } from '../../context/notification-context'
import {
  CONFLICT_MESSAGE,
  FlagsConflictError
} from '../../hooks/use-feature-flags'
import { classNames, extractErrorMessage } from '../../helper'
import { STAGE, needsProductionConfirm, stageLabel } from '../../helper/stage'
import {
  FLAG_STATES,
  PRICING_FLAG_KEY,
  PRODUCTION_STAGE,
  buildPricingEnforcementFlag,
  describePricingFlag,
  pricingFlagOf,
  pricingFlagWarnings,
  setPartnerOverride
} from '../../helper/pricing-flag'

export { PRODUCTION_STAGE }

// Auswahl im Control. rollout heißt kein Override, das Flag entscheidet.
export const OVERRIDE_CHOICES = [
  {
    value: 'rollout',
    label: 'Wie Rollout',
    description: 'Kein Override, es gilt das Flag.'
  },
  {
    value: 'on',
    label: 'An',
    description: 'Sperre für diesen Kunden an.'
  },
  {
    value: 'off',
    label: 'Aus',
    description: 'Sperre für diesen Kunden aus.'
  }
]

const CHOICE_LABELS = Object.fromEntries(
  OVERRIDE_CHOICES.map(({ value, label }) => [value, label])
)

const STATE_LABELS = {
  [FLAG_STATES.ON]: 'an',
  [FLAG_STATES.OFF]: 'aus',
  [FLAG_STATES.PARTIAL]: 'nicht berechenbar'
}

const REASON_LABELS = {
  missing: 'Flag fehlt',
  surface: 'Surface lp',
  winner: 'Gewinner gesetzt',
  draft: 'Flag im Entwurf',
  paused: 'Flag pausiert',
  concluded: 'Flag beendet',
  targeting: 'Targeting schließt den Kunden aus',
  targeting_unknown: 'Targeting nicht prüfbar',
  unit: 'Unit ist nicht partner',
  no_unit: 'Partner-ID fehlt',
  override: 'Override',
  experiment: 'Experiment statt Rollout',
  rollout: 'Rollout',
  rollout_partial: 'Teil-Rollout'
}

const STAGE_TONES = {
  production: 'bg-red-600 text-white ring-red-700',
  staging: 'bg-amber-100 text-amber-900 ring-amber-600/30',
  develop: 'bg-gray-100 text-gray-700 ring-gray-500/20'
}

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/**
 * Aktuelle Auswahl aus dem Flag. Ein Override außer on oder off zählt wie
 * kein Override, denn das Dashboard ignoriert ihn auch.
 */
export const overrideChoiceOf = (flag, partnerId) => {
  const value = isPlainObject(flag?.overrides)
    ? flag.overrides[partnerId]
    : undefined
  return value === 'on' || value === 'off' ? value : 'rollout'
}

/**
 * Text zum Ergebnis von describePricingFlag, z. B. „an (Override)" oder
 * „aus (Rollout 0 %)".
 */
export const describeFlagResult = ({ state, reason, percent }) => {
  const reasonLabel = REASON_LABELS[reason] || reason
  const withPercent =
    (reason === 'rollout' || reason === 'rollout_partial') &&
    typeof percent === 'number'
      ? `${reasonLabel} ${percent} %`
      : reasonLabel
  return `${STATE_LABELS[state] || state} (${withPercent})`
}

/**
 * Nächster Stand der Flags-Map für eine Auswahl. Fehlt das Flag, wird es aus
 * der Vorlage angelegt (Rollout mit 0 %, wirkt nur über Overrides).
 *
 * @param {object} base geladener Stand (parseFlagsSystem)
 * @param {{ partnerId: string, choice: 'rollout'|'on'|'off', owner?: string,
 *   now?: Date }} params
 * @returns {{ nextFlags: object, created: boolean }}
 */
export const nextFlagsFor = (
  base,
  { partnerId, choice, owner = '', now = new Date() }
) => {
  const flags = isPlainObject(base?.flags) ? base.flags : {}
  const existing = pricingFlagOf(base)
  const created = !existing

  if (created && choice === 'rollout') {
    return { nextFlags: flags, created: false }
  }

  const withFlag = created
    ? {
        ...flags,
        [PRICING_FLAG_KEY]: buildPricingEnforcementFlag({ owner, now })
      }
    : flags

  return {
    nextFlags: setPartnerOverride(
      withFlag,
      partnerId,
      choice === 'rollout' ? null : choice
    ),
    created
  }
}

export const StageBadge = ({ stage, className }) => (
  <span
    className={classNames(
      'inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset',
      // Unbekannte Stage wie production, der Schreibschutz gilt auch dort
      STAGE_TONES[stage] || STAGE_TONES.production,
      className
    )}
    data-testid='stage-badge'
  >
    {stageLabel(stage)}
  </span>
)

const ConfirmBody = ({
  pending,
  stage,
  partnerName,
  hasPlanPayment,
  isSaving,
  onCancel,
  onConfirm
}) => {
  const isProduction = needsProductionConfirm(stage)
  const [step, setStep] = useState(1)
  const [typed, setTyped] = useState('')

  const { before, after, created, warnings } = pending

  return (
    <div>
      <div className='flex items-start justify-between gap-4'>
        <h3 className='text-base font-semibold leading-6 text-gray-900'>
          Pricing-Sperre für {partnerName || 'diesen Kunden'} ändern
        </h3>
        <StageBadge stage={stage} />
      </div>

      <div className='mt-4 space-y-3 text-sm text-gray-700'>
        <dl className='grid grid-cols-3 gap-x-4 gap-y-2'>
          <dt className='text-gray-500'>Auswahl</dt>
          <dd className='col-span-2'>
            {CHOICE_LABELS[pending.from]} wird {CHOICE_LABELS[pending.to]}
          </dd>
          <dt className='text-gray-500'>Schalter vorher</dt>
          <dd className='col-span-2'>{describeFlagResult(before)}</dd>
          <dt className='text-gray-500'>Schalter nachher</dt>
          <dd className='col-span-2 font-medium text-gray-900'>
            {describeFlagResult(after)}
          </dd>
        </dl>

        <p>
          Ändert FEATURE_FLAGS auf {stageLabel(stage)}. Das Dashboard übernimmt
          es beim nächsten Laden, offene Tabs spätestens nach 10 Minuten. Die
          Sperre greift nur bei Kunden mit Paket-Zahlung.
        </p>

        {!hasPlanPayment && (
          <Notice tone='info'>
            Dieser Kunde hat noch keine Paket-Zahlung. Für ihn ändert sich erst
            etwas, wenn ein Paket hinterlegt ist.
          </Notice>
        )}

        {created && (
          <Notice tone='warning'>
            Das Flag {PRICING_FLAG_KEY} fehlt auf {stageLabel(stage)}. Es wird
            aus der Vorlage angelegt, als Rollout mit 0 %, und wirkt damit nur
            über Overrides.
          </Notice>
        )}

        {warnings.length > 0 && (
          <Notice tone='warning'>
            <ul className='list-inside list-disc'>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Notice>
        )}

        {isProduction && step === 2 && (
          <div className='rounded-md bg-red-50 p-3 ring-1 ring-inset ring-red-600/20'>
            <div className='flex gap-2'>
              <ExclamationTriangleIcon
                className='mt-0.5 h-5 w-5 flex-shrink-0 text-red-600'
                aria-hidden='true'
              />
              <div className='text-red-800'>
                <p className='font-medium'>
                  {stage === PRODUCTION_STAGE
                    ? 'Das ist production.'
                    : 'Die Stage ist unbekannt und wird wie production behandelt.'}{' '}
                  Der Kunde sieht die Änderung beim nächsten Laden des
                  Dashboards.
                </p>
                <label
                  htmlFor='pricing-override-confirm'
                  className='mt-2 block text-xs'
                >
                  Gib {PRODUCTION_STAGE} ein, um zu bestätigen
                </label>
                <input
                  id='pricing-override-confirm'
                  type='text'
                  autoComplete='off'
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className='mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-red-300 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm'
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className='mt-6 flex justify-end gap-3'>
        <Button.SecondaryLG
          text='Abbrechen'
          onClick={onCancel}
          disabled={isSaving}
        />
        {isProduction && step === 1 ? (
          <Button.PrimaryLG text='Weiter' onClick={() => setStep(2)} />
        ) : (
          <Button.PrimaryLG
            text={isProduction ? 'Auf production speichern' : 'Speichern'}
            onClick={onConfirm}
            isLoading={isSaving}
            disabled={isProduction && typed.trim() !== PRODUCTION_STAGE}
          />
        )}
      </div>
    </div>
  )
}

/**
 * @param {object} props
 * @param {string} props.partnerId
 * @param {string} [props.partnerName]
 * @param {object} props.flagsQuery flagsQuery aus useFeatureFlags
 * @param {(base: object, next: object) => Promise<object>} props.saveFlags
 * @param {boolean} props.isAdmin nur dann rendert das Control
 * @param {string} [props.owner] owner, falls das Flag angelegt werden muss
 * @param {boolean} [props.hasPlanPayment]
 * @param {string} [props.stage]
 */
const PricingOverrideControl = ({
  partnerId,
  partnerName,
  flagsQuery,
  saveFlags,
  isAdmin,
  owner = '',
  hasPlanPayment = false,
  stage = STAGE,
  className
}) => {
  const { success, error } = useNotificationContext()
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  if (!isAdmin) return null

  const base = flagsQuery?.data
  const flag = base ? pricingFlagOf(base) : undefined
  const current = overrideChoiceOf(flag, partnerId)
  const blocked = !base || base.parseError || isSaving
  const ctx = { stage }

  const onSelect = (choice) => {
    if (blocked || choice === current) return
    const { nextFlags, created } = nextFlagsFor(base, {
      partnerId,
      choice,
      owner
    })
    const nextFlag = nextFlags[PRICING_FLAG_KEY]

    setPending({
      id: Date.now(),
      // Gespeichert wird genau dieser Stand, den der Dialog zeigt
      base,
      nextFlags,
      from: current,
      to: choice,
      created,
      before: describePricingFlag(flag, partnerId, ctx),
      after: describePricingFlag(nextFlag, partnerId, ctx),
      warnings: pricingFlagWarnings(nextFlag)
    })
    setOpen(true)
  }

  const onConfirm = async () => {
    setIsSaving(true)
    try {
      // Auf dem Stand, den der Dialog gezeigt hat. Hat sich FEATURE_FLAGS
      // seitdem geändert (Refetch, anderer Admin), greift die Bedingung auf
      // updatedAt und es kommt ein Konflikt statt einer ungeprüften Änderung.
      await saveFlags(pending.base, pending.nextFlags)
      success(
        `Pricing-Sperre für ${partnerName || partnerId} auf ${stageLabel(stage)} gespeichert (${CHOICE_LABELS[pending.to]}).`
      )
      setOpen(false)
    } catch (err) {
      if (err instanceof FlagsConflictError) {
        error(CONFLICT_MESSAGE)
        setOpen(false)
      } else {
        error(`Speichern fehlgeschlagen. ${extractErrorMessage(err)}`)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const result = flag !== undefined && describePricingFlag(flag, partnerId, ctx)
  const warnings = flag ? pricingFlagWarnings(flag) : []

  return (
    <div className={className}>
      <div className='flex flex-wrap items-center gap-2'>
        <h3 className='text-sm font-medium text-gray-900'>
          Pricing-Sperre für diesen Kunden
        </h3>
        <StageBadge stage={stage} />
        <span className='text-xs text-gray-500'>Nur für Admins</span>
      </div>

      {flagsQuery?.isLoading && (
        <div
          className='mt-3 h-9 w-72 animate-pulse rounded-md bg-gray-200'
          aria-label='FEATURE_FLAGS wird geladen'
        />
      )}

      {flagsQuery?.isError && (
        <Notice tone='error' className='mt-3'>
          FEATURE_FLAGS konnte nicht geladen werden.{' '}
          {extractErrorMessage(flagsQuery.error)}{' '}
          <button
            type='button'
            onClick={() => flagsQuery.refetch()}
            className='font-medium underline'
          >
            Erneut laden
          </button>
        </Notice>
      )}

      {base?.parseError && (
        <Notice tone='error' className='mt-3'>
          FEATURE_FLAGS enthält kein gültiges JSON. Speichern ist gesperrt,
          damit nichts überschrieben wird.
        </Notice>
      )}

      {base && !base.parseError && (
        <>
          <RadioGroup
            value={current}
            onChange={onSelect}
            disabled={blocked}
            className='mt-3'
          >
            <RadioGroup.Label className='sr-only'>
              Pricing-Sperre für diesen Kunden
            </RadioGroup.Label>
            <div className='inline-flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300'>
              {OVERRIDE_CHOICES.map((choice, index) => (
                <RadioGroup.Option
                  key={choice.value}
                  value={choice.value}
                  title={choice.description}
                  className={({ checked }) =>
                    classNames(
                      'cursor-pointer px-3 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600',
                      index === 0 && 'rounded-l-md',
                      index === OVERRIDE_CHOICES.length - 1 && 'rounded-r-md',
                      index > 0 && 'border-l border-gray-300',
                      checked
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-900 hover:bg-gray-50',
                      blocked && 'cursor-not-allowed opacity-60'
                    )
                  }
                >
                  <RadioGroup.Label as='span'>{choice.label}</RadioGroup.Label>
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>

          <p className='mt-2 text-xs text-gray-500'>
            {result
              ? `Schalter für diesen Kunden ${describeFlagResult(result)}.`
              : null}{' '}
            {flag === null &&
              `Das Flag ${PRICING_FLAG_KEY} fehlt auf ${stageLabel(stage)}. Mit An oder Aus wird es aus der Vorlage angelegt.`}
          </p>

          {warnings.length > 0 && (
            <ul className='mt-2 list-inside list-disc text-xs text-amber-800'>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
        </>
      )}

      <Modal open={open} setOpen={setOpen} size='lg' disableClose={isSaving}>
        {pending && (
          <ConfirmBody
            key={pending.id}
            pending={pending}
            stage={stage}
            partnerName={partnerName}
            hasPlanPayment={hasPlanPayment}
            isSaving={isSaving}
            onCancel={() => setOpen(false)}
            onConfirm={onConfirm}
          />
        )}
      </Modal>
    </div>
  )
}

export default PricingOverrideControl
